import {
  HighwayMaintenanceFeePenaltyCardList,
  HighwayMaintenanceFeePenaltyCard,
  HighwayMaintenanceFeePenaltyTicketDetailPanel,
  FillQueryResultDialog,
  useFillQueryResultDialog,
  ConfirmPaymentOnBehalfDialog,
  useConfirmPaymentOnBehalfDialog,
  useChargeFailDialog,
  ChargeFailDialog,
  HighwayMaintenanceFeePenaltyTicketSearchBar,
  HighwayMaintenanceFeePenaltyTicketSearchBarProps,
  useTicketSearchBarParams,
} from '#/components/ui/tickets/highway-maintenance-fee-penalty/components';
import { useThrottledValue } from '@mantine/hooks';
import {
  ActionIcon,
  Box,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import {
  useChargeTicket,
  useCompleteTicket,
  useExportCompletedTicket,
  useHighwayMaintenanceFeePenaltyTickets,
  useMarkNoFee,
  useMarkQueryFailed,
} from '#/components/ui/tickets/highway-maintenance-fee-penalty/endpoints/ticketsHooks';
import { useMemo, useState } from 'react';
import { TICKET_TYPE, TICKET_MODE } from '../../config';
import Fuse from 'fuse.js';
import { ListTicketsParams } from '../../highway-maintenance-fee-penalty/endpoints/tickets';
import { TabPanel } from '../TicketsTabbar';
import { HIGHWAY_MAINTENANCE_FEE_PENALTY_STATE_VALUE } from '../../highway-maintenance-fee-penalty/model';
import { useScheduleEnrollment } from '../../highway-maintenance-fee-penalty/endpoints/enrollmentsHooks';
import { addDays } from 'date-fns';
import { ExportCompletedTicketButton } from '../exportExcel/ExportCompletedTicketButton';
import { toStartOfDayISO, toEndOfDayISO } from '../../utils';

export function HighwayMaintenanceFeePenaltyPanel({
  mode,
  defaultOptions,
  availField,
  availStates = HIGHWAY_MAINTENANCE_FEE_PENALTY_STATE_VALUE,
}: {
  mode: TICKET_MODE;
} & Partial<HighwayMaintenanceFeePenaltyTicketSearchBarProps>) {
  const isHistoryTicketMode = mode === TICKET_MODE.HISTORY;
  const { params } = useTicketSearchBarParams();

  const queryStates = availStates?.filter((s) => {
    const filterStates = params.state.length === 0 ? availStates : params.state;
    return filterStates.includes(s);
  });

  const ticketsQueryParam: ListTicketsParams = {
    state: queryStates,
    createDateTimeFrom: params.fromDate
      ? toStartOfDayISO(params.fromDate)
      : undefined,
    createDateTimeTo: params.toDate ? toEndOfDayISO(params.toDate) : undefined,
  };

  const { data, isLoading, refetch } =
    useHighwayMaintenanceFeePenaltyTickets(ticketsQueryParam);

  const {
    mutate: exportCompletedTicket,
    isPending: isExportingCompletedTicket,
  } = useExportCompletedTicket();
  const { mutate: markQueryFailed } = useMarkQueryFailed();
  const { mutate: markNoFee } = useMarkNoFee();
  const { mutate: chargeTicket } = useChargeTicket();
  const { mutate: completeTicket } = useCompleteTicket();
  const { mutateAsync: scheduleEnrollment } = useScheduleEnrollment();

  const {
    props: confirmPaymentOnBehalfDialogProps,
    confirm: openConfirmDialog,
  } = useConfirmPaymentOnBehalfDialog();
  const { props: fillQueryResultDialogProps, open: openFillQueryResultDialog } =
    useFillQueryResultDialog();
  const { props: chargeFailDialogProps, open: openChargeFailDialog } =
    useChargeFailDialog();

  const throttledSearchText = useThrottledValue(
    params.fuzzySearchText ?? '',
    1000,
  );

  const tickets = useMemo(() => data?.data ?? [], [data]);

  const searchedResult = useMemo(() => {
    if (!throttledSearchText.length) {
      return tickets.sort(({ updateDateTime: a }, { updateDateTime: b }) =>
        a.localeCompare(b),
      );
    }

    const fuse = new Fuse(tickets, {
      keys: ['plateNumber', 'account.email', 'idNumber', 'taxIdNumber'],
      threshold: 0.0,
      distance: 100,
      ignoreLocation: true,
      includeScore: true,
    });

    return fuse
      .search(throttledSearchText)
      .sort(({ score: a }, { score: b }) => (a ?? 0) - (b ?? 0))
      .map(({ item }) => item);
  }, [tickets, throttledSearchText]);

  const [currentDetailIndex, setCurrentDetailIndex] = useState<
    number | undefined
  >(undefined);

  const ticket =
    currentDetailIndex === undefined
      ? undefined
      : searchedResult[currentDetailIndex];

  const currentDetailId = ticket?.id;
  const currentDetailPaymentId = ticket?.paymentId || null;

  return (
    <>
      <Box mt={20}>
        <TabPanel tabKey={TICKET_TYPE.HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKETS}>
          <Stack gap="sm">
            <Paper withBorder bg="gray.1" radius="md" shadow="none" p="sm">
              <HighwayMaintenanceFeePenaltyTicketSearchBar
                defaultOptions={{ ...defaultOptions }}
                availField={availField}
                availStates={availStates}
              />
              <Group gap="xs" mt="xs">
                <Text
                  size="sm"
                  c="dimmed"
                >{`條件下結果共 ${searchedResult.length} 筆`}</Text>
                <Tooltip label="重新整理">
                  <ActionIcon
                    variant="subtle"
                    color="dimmed"
                    loading={isLoading}
                    onClick={() => refetch()}
                  >
                    <IconRefresh size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Paper>

            {isHistoryTicketMode && (
              <ExportCompletedTicketButton
                ticketType={TICKET_TYPE.HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKETS}
                loading={isExportingCompletedTicket}
                onExportSubmit={exportCompletedTicket}
              />
            )}

            <HighwayMaintenanceFeePenaltyCardList
              isLoading={isLoading}
              isEmpty={tickets.length < 1}
            >
              {searchedResult.map((ticket, index) => (
                <HighwayMaintenanceFeePenaltyCard
                  key={ticket.id}
                  {...ticket}
                  onDetailClick={() => setCurrentDetailIndex(index)}
                  onConfirmPaymentClick={async () => {
                    const res = await openConfirmDialog(ticket);
                    if (!res) return;
                    completeTicket({
                      id: ticket.id,
                      completeDateTime: new Date().toISOString(),
                    });
                  }}
                  onFillQueryResultClick={async () => {
                    const res = await openFillQueryResultDialog(ticket);
                    if (!res) return;

                    const [result, close] = res;
                    if (!result) return;

                    if (result.type === 'query_failed') {
                      markQueryFailed({ id: ticket.id, reason: result.reason });
                      close();
                      return;
                    }
                    if (result.type === 'no_fee') {
                      markNoFee({ id: ticket.id, reason: '無費用' });
                      close();
                      return;
                    }
                    if (result.type === 'fee_required') {
                      chargeTicket(
                        {
                          id: ticket.id,
                          amount: result.amount,
                          handlingFee: result.handlingFee,
                        },
                        {
                          onSuccess() {
                            close();
                          },
                          async onError(_response, { amount: chargeAmount }) {
                            close();
                            const chargeFailResult = await openChargeFailDialog(
                              ticket,
                              chargeAmount,
                            );
                            if (
                              chargeFailResult &&
                              chargeFailResult.afterDays
                            ) {
                              const scheduledDate = addDays(
                                new Date(),
                                chargeFailResult.afterDays,
                              ).toISOString();
                              await scheduleEnrollment({
                                scheduledDate,
                                enrollmentId: ticket.hmfpAutoPayEnrollmentId,
                                ticketId: ticket.id,
                              });
                            }
                          },
                        },
                      );
                    }
                  }}
                />
              ))}
            </HighwayMaintenanceFeePenaltyCardList>
          </Stack>
        </TabPanel>
      </Box>
      <HighwayMaintenanceFeePenaltyTicketDetailPanel
        id={currentDetailId}
        currentIndex={currentDetailIndex}
        total={searchedResult.length}
        setCurrentDetailIndex={setCurrentDetailIndex}
        paymentId={currentDetailPaymentId}
      />
      <ConfirmPaymentOnBehalfDialog {...confirmPaymentOnBehalfDialogProps} />
      <FillQueryResultDialog {...fillQueryResultDialogProps} />
      <ChargeFailDialog {...chargeFailDialogProps} />
    </>
  );
}
